import axios from 'axios';
import toast from 'react-hot-toast';

// Base API configuration is already set in AuthContext.js
// axios.defaults.baseURL = isProduction ? '/api' : 'http://localhost:5002/api';
// axios.defaults.withCredentials = true;

// Retry logic for handling 429 errors
const retryWithDelay = async (fn, retries = 3, delay = 1000, backoff = 1.5) => {
  try {
    return await fn();
  } catch (error) {
    // If it's a 429 error and we have retries left
    if (error?.response?.status === 429 && retries > 0) {
      // Wait for the specified delay
      await new Promise(resolve => setTimeout(resolve, delay));
      // Retry with increased delay (backoff)
      return retryWithDelay(fn, retries - 1, delay * backoff, backoff);
    }
    // For other errors or if we're out of retries, throw
    throw error;
  }
};

// Generic API service
const api = {
  // Generic GET request
  get: async (endpoint, params = {}) => {
    try {
      const makeRequest = () => axios.get(endpoint, { params });
      const response = await retryWithDelay(makeRequest);
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      throw error;
    }
  },

  // Generic POST request
  post: async (endpoint, data = {}) => {
    try {
      const makeRequest = () => axios.post(endpoint, data);
      const response = await retryWithDelay(makeRequest);
      return response.data;
    } catch (error) {
      console.error(`Error posting to ${endpoint}:`, error);
      throw error;
    }
  },

  // Generic PUT request
  put: async (endpoint, data = {}) => {
    try {
      const makeRequest = () => axios.put(endpoint, data);
      const response = await retryWithDelay(makeRequest);
      return response.data;
    } catch (error) {
      console.error(`Error updating ${endpoint}:`, error);
      throw error;
    }
  },

  // Generic DELETE request
  delete: async (endpoint) => {
    try {
      const makeRequest = () => axios.delete(endpoint);
      const response = await retryWithDelay(makeRequest);
      return response.data;
    } catch (error) {
      console.error(`Error deleting ${endpoint}:`, error);
      throw error;
    }
  },

  // Generic PATCH request
  patch: async (endpoint, data = {}) => {
    try {
      const makeRequest = () => axios.patch(endpoint, data);
      const response = await retryWithDelay(makeRequest);
      return response.data;
    } catch (error) {
      console.error(`Error patching ${endpoint}:`, error);
      throw error;
    }
  }
};

// Dashboard API
export const dashboardAPI = {
  getOverview: () => api.get('/dashboard/overview'),
  getAnalytics: (params) => api.get('/dashboard/analytics', { params }),
  getNotifications: () => api.get('/dashboard/notifications'),
};

// Members API
export const membersAPI = {
  getAll: (params) => api.get('/members', { params }),
  getById: (id) => api.get(`/members/${id}`),
  create: (data) => api.post('/members', data),
  update: (id, data) => api.put(`/members/${id}`, data),
  delete: (id) => api.delete(`/members/${id}`),
  getBySearch: (query) => api.get(`/members/search?q=${query}`),
  checkIn: (id) => api.post(`/members/${id}/check-in`),
  checkOut: (id) => api.post(`/members/${id}/check-out`),
  getMembershipStatus: (id) => api.get(`/members/${id}/membership`),
};

// Payments API
export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  getByMemberId: (memberId) => api.get(`/payments/member/${memberId}`),
  getStats: (params) => api.get('/payments/stats/overview', { params }),
  getFinancialReport: (params) => api.get('/payments/reports/financial', { params }),
  generateInvoice: (id) => api.get(`/payments/invoices/${id}/pdf`),
  cancelPayment: (id, reason) => api.patch(`/payments/${id}/cancel`, { reason }),
};

// Sessions API
export const sessionsAPI = {
  getAll: (params) => api.get('/sessions/attendance', { params }),
  getById: (id) => api.get(`/sessions/${id}`),
  checkIn: (data) => api.post('/sessions/attendance', { ...data, type: 'check-in' }),
  checkOut: (data) => api.post('/sessions/attendance', { ...data, type: 'check-out' }),
  getTodayAttendance: () => api.get('/sessions/attendance/today'),
  getStats: (params) => api.get('/sessions/attendance/stats', { params }),
  getAttendanceReport: (params) => api.get('/sessions/reports/attendance', { params }),
};

// Courses API
export const coursesAPI = {
  getAll: (params) => api.get('/courses', { params }),
  getById: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
  getCategories: () => api.get('/courses/categories/list'),
};

// Trainers API
export const trainersAPI = {
  getAll: (params) => api.get('/trainers', { params }),
  getById: (id) => api.get(`/trainers/${id}`),
  create: (data) => api.post('/trainers', data),
  update: (id, data) => api.put(`/trainers/${id}`, data),
  delete: (id) => api.delete(`/trainers/${id}`),
  getSpecializations: () => api.get('/trainers/specializations/list'),
};

// Schedule API
export const scheduleAPI = {
  getAll: (params) => api.get('/schedule', { params }),
  getById: (id) => api.get(`/schedule/${id}`),
  create: (data) => api.post('/schedule', data),
  update: (id, data) => api.put(`/schedule/${id}`, data),
  cancel: (id, reason) => api.patch(`/schedule/${id}/cancel`, { reason }),
  getCalendarView: (params) => api.get('/schedule/calendar/view', { params }),
};

// Classrooms API
export const classroomsAPI = {
  getAll: (params) => api.get('/classrooms', { params }),
  getById: (id) => api.get(`/classrooms/${id}`),
  create: (data) => api.post('/classrooms', data),
  update: (id, data) => api.put(`/classrooms/${id}`, data),
  delete: (id) => api.delete(`/classrooms/${id}`),
  getAvailability: (id, date) => api.get(`/classrooms/${id}/availability`, { params: { date } }),
};

// Benefits API
export const benefitsAPI = {
  getAll: (params) => api.get('/benefits', { params }),
  getById: (id) => api.get(`/benefits/${id}`),
  create: (data) => api.post('/benefits', data),
  update: (id, data) => api.put(`/benefits/${id}`, data),
  delete: (id) => api.delete(`/benefits/${id}`),
  getSummary: (params) => api.get('/benefits/summary/financial', { params }),
  getTrends: (params) => api.get('/benefits/trends/monthly', { params }),
};

// Reports API
export const reportsAPI = {
  getAll: (params) => api.get('/reports', { params }),
  getById: (id) => api.get(`/reports/${id}`),
  create: (data) => api.post('/reports', data),
  update: (id, data) => api.put(`/reports/${id}`, data),
  delete: (id) => api.delete(`/reports/${id}`),
  generateFinancial: (data) => api.post('/reports/generate/financial', data),
  generateAttendance: (data) => api.post('/reports/generate/attendance', data),
  getRecentReports: (limit = 5) => api.get(`/reports?limit=${limit}`),
  exportReport: (id, format) => api.get(`/reports/${id}/export?format=${format}`),
};

// Certifications API
export const certificationsAPI = {
  getAll: (params) => api.get('/certifications', { params }),
  getById: (id) => api.get(`/certifications/${id}`),
  create: (data) => api.post('/certifications', data),
  update: (id, data) => api.put(`/certifications/${id}`, data),
  delete: (id) => api.delete(`/certifications/${id}`),
  getByStudent: (studentId) => api.get(`/certifications/student/${studentId}`),
  download: (id) => api.get(`/certifications/${id}/download`),
};

// Equipment API
export const equipmentAPI = {
  getAll: (params) => api.get('/equipments', { params }),
  getById: (id) => api.get(`/equipments/${id}`),
  create: (data) => api.post('/equipments', data),
  update: (id, data) => api.put(`/equipments/${id}`, data),
  delete: (id) => api.delete(`/equipments/${id}`),
  updateStatus: (id, status) => api.patch(`/equipments/${id}/status`, { status }),
  scheduleMaintenance: (id, data) => api.post(`/equipments/${id}/maintenance`, data),
  getLowStock: () => api.get('/equipments?status=low_stock'),
  getNeedsMaintenance: () => api.get('/equipments?status=maintenance_needed'),
};

// Settings API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  getBusinessHours: () => api.get('/settings/business-hours'),
  updateBusinessHours: (data) => api.put('/settings/business-hours', data),
  getNotificationSettings: () => api.get('/settings/notifications'),
  updateNotificationSettings: (data) => api.put('/settings/notifications', data),
  getLanguagePreference: () => api.get('/settings/language'),
  saveLanguagePreference: (language) => api.post('/settings/language', { language }),
  getThemePreference: () => api.get('/settings/theme'),
  saveThemePreference: (theme) => api.post('/settings/theme', { theme }),
  resetToDefault: () => api.post('/settings/reset'),
};

// Subscriptions API
export const subscriptionsAPI = {
  getAll: (params) => api.get('/subscriptions', { params }),
  getById: (id) => api.get(`/subscriptions/${id}`),
  create: (data) => api.post('/subscriptions', data),
  update: (id, data) => api.put(`/subscriptions/${id}`, data),
  delete: (id) => api.delete(`/subscriptions/${id}`),
  getByMemberId: (memberId) => api.get(`/subscriptions/member/${memberId}`),
  getActive: () => api.get('/subscriptions?status=active'),
  getExpiring: (days = 7) => api.get(`/subscriptions/expiring?days=${days}`),
  renew: (id, data) => api.post(`/subscriptions/${id}/renew`, data),
  cancel: (id, reason) => api.patch(`/subscriptions/${id}/cancel`, { reason }),
  pause: (id, data) => api.patch(`/subscriptions/${id}/pause`, data),
  resume: (id) => api.patch(`/subscriptions/${id}/resume`),
};

export default api;